import type React from 'react';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

interface TouchOptimizerProps {
  children: React.ReactNode;
  enableFastClick?: boolean;
  enableScrollSmoothing?: boolean;
  preventZoom?: boolean;
  optimizeRendering?: boolean;
}

const TouchContainer = styled.div<{
  $enableFastClick: boolean;
  $preventZoom: boolean;
}>`
  ${({ $enableFastClick }) =>
    $enableFastClick &&
    `
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  `}

  ${({ $preventZoom }) =>
    $preventZoom &&
    `
    touch-action: pan-x pan-y pinch-zoom;
  `}
`;

// Keep native touch behavior. Older versions synthesized clicks here, which
// could activate controls twice and prevented text selection across the app.
const useTouchOptimization = (
  containerRef: React.RefObject<HTMLDivElement>,
  options: {
    enableFastClick: boolean;
    enableScrollSmoothing: boolean;
    optimizeRendering: boolean;
  }
) => {
  const { optimizeRendering } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = () => {
      if (optimizeRendering) {
        // Disable animations during touch
        document.documentElement.style.setProperty('--touch-active', '1');
      }
    };

    const handleTouchEnd = () => {
      if (optimizeRendering) {
        // Re-enable animations after touch
        document.documentElement.style.setProperty('--touch-active', '0');
      }
    };

    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [optimizeRendering, containerRef]);
};

// Battery optimization hook
const useBatteryOptimization = () => {
  useEffect(() => {
    // @ts-ignore - Battery API is experimental
    if ('getBattery' in navigator) {
      // @ts-ignore
      navigator.getBattery().then((battery: any) => {
        const updatePerformanceMode = () => {
          const isLowBattery = battery.level < 0.2;
          const isCharging = battery.charging;
          
          // Reduce animations and effects when battery is low
          if (isLowBattery && !isCharging) {
            document.documentElement.style.setProperty('--reduce-motion', '1');
            document.documentElement.style.setProperty('--low-battery', '1');
          } else {
            document.documentElement.style.setProperty('--reduce-motion', '0');
            document.documentElement.style.setProperty('--low-battery', '0');
          }
        };

        updatePerformanceMode();
        battery.addEventListener('levelchange', updatePerformanceMode);
        battery.addEventListener('chargingchange', updatePerformanceMode);

        return () => {
          battery.removeEventListener('levelchange', updatePerformanceMode);
          battery.removeEventListener('chargingchange', updatePerformanceMode);
        };
      });
    }

    // Fallback: detect power mode from CPU performance
    const detectPowerMode = () => {
      const startTime = performance.now();
      const iterations = 100000;
      
      // Simple CPU benchmark
      for (let i = 0; i < iterations; i++) {
        Math.random();
      }
      
      const endTime = performance.now();
      const executionTime = endTime - startTime;
      
      // If execution is slow, assume power saving mode
      if (executionTime > 50) {
        document.documentElement.style.setProperty('--power-save-mode', '1');
      }
    };

    detectPowerMode();
  }, []);
};

// Memory optimization hook
const useMemoryOptimization = () => {
  useEffect(() => {
    // @ts-ignore - Memory API is experimental
    if ('memory' in performance) {
      const checkMemoryPressure = () => {
        // @ts-ignore
        const memInfo = performance.memory;
        const memoryUsageRatio = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;
        
        if (memoryUsageRatio > 0.8) {
          // High memory usage - reduce quality
          document.documentElement.style.setProperty('--memory-pressure', '1');
          
          // Suggest garbage collection (if available)
          if ('gc' in window) {
            // @ts-ignore
            window.gc();
          }
        } else {
          document.documentElement.style.setProperty('--memory-pressure', '0');
        }
      };

      checkMemoryPressure();
      const interval = setInterval(checkMemoryPressure, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, []);
};

export const TouchOptimizer: React.FC<TouchOptimizerProps> = ({
  children,
  enableFastClick = true,
  enableScrollSmoothing = true,
  preventZoom = false,
  optimizeRendering = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useTouchOptimization(containerRef, {
    enableFastClick,
    enableScrollSmoothing,
    optimizeRendering,
  });

  useBatteryOptimization();
  useMemoryOptimization();

  return (
    <TouchContainer
      ref={containerRef}
      $enableFastClick={enableFastClick}
      $preventZoom={preventZoom}
    >
      {children}
    </TouchContainer>
  );
};

export default TouchOptimizer;
